/**
 * Volwaardige onboarding-wizard — flow CalqTool in stappen; persistentie via localStorage.
 */

import { useState, useEffect, type ReactNode } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowRight,
  BarChart3,
  CheckCircle2,
  FileStack,
  FolderKanban,
  Sparkles,
  Upload,
} from 'lucide-react';
import { AnimatedSection } from '../components/AnimatedSection';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { SeoMeta } from '../components/SeoMeta';
import {
  isOnboardingDone,
  markOnboardingComplete,
  markOnboardingSkipped,
  resetOnboarding,
} from '../lib/onboarding-storage';

type StepDef = {
  id: string;
  title: string;
  lead: string;
  Icon: typeof FolderKanban;
  bullets: string[];
  tip?: string;
};

/** `**vet**` in bulletteksten → <strong> (eenvoudige markup voor onboarding). */
function renderBulletWithBold(text: string): ReactNode {
  return text.split(/(\*\*[^*]+\*\*)/g).map((chunk, i) => {
    const inner = /^\*\*([^*]+)\*\*$/.exec(chunk);
    if (inner) {
      return (
        <strong key={i} className="font-semibold text-text">
          {inner[1]}
        </strong>
      );
    }
    return <span key={i}>{chunk}</span>;
  });
}

const STEPS: StepDef[] = [
  {
    id: 'welcome',
    title: 'Welkom bij CalqTool™',
    lead: 'Je werkt met een vaste prijsbibliotheek (library): elke begrotingspost koppelt aan systeem + variant. Zo blijven totalen reproduceerbaar, uitlegbaar en vergelijkbaar — precies waar je straks op wilt sturen.',
    Icon: Sparkles,
    bullets: [
      'Geen losse Excel-regels zonder onderbouwing: elke post heeft een bron in de library.',
      'Gericht op renovaties, transformaties en splitsingen — van eerste raming tot uitwerkbare posten.',
      'Je start met een dossier; alles daarna speelt zich af op één overzichtelijke projectpagina.',
    ],
  },
  {
    id: 'dossier',
    title: 'Stap 1 — Gegevens & dossier',
    lead: 'Via “Nieuw dossier” zet je in twee korte stappen de basis neer: naam en klanttype, daarna projecttype, gebouw en postcode. Daarna werk je verder op het dossier onder tab **Gegevens**.',
    Icon: FolderKanban,
    bullets: [
      '**Klanttype** stemt o.a. toon, tips en het detailniveau op tab **Resultaat** op jouw situatie af — zonder dat je ergens “technische modus” hoeft te zoeken.',
      'Onder Gegevens kun je ook **documenten in de bibliotheek** zetten (uploads, referenties) en — als je wilt — **tekening of PDF laten analyseren**.',
      '**Postcode en regio** gebruiken we voor locatie en prijspeil; het **indexjaar** past CalqTool centraal toe.',
      'Op de projectpagina loop je **vier vaste stappen** (tabs): Gegevens → Posten → Resultaat → Rapportage. De tabs **blijven bij scrollen plakken** onder het menu; in de **zijkolom** kun je dezelfde route **aanklikken** — één mentale lijn, twee manieren om te navigeren.',
    ],
    tip: 'Na “Dossier aanmaken en verder” opent automatisch je nieuwe project op Gegevens.',
  },
  {
    id: 'posten',
    title: 'Stap 2 — Posten (prijsboek)',
    lead: 'Op tab **Posten** vul je het prijsboek: hoofdgroep, eventueel subgroep, zoeken op trefwoord, dan bouwdeel + variant + hoeveelheid. In stap 3 van de flow kun je regels finetunen, ordenen en **bouwclusters** gelijk trekken voor een helder resultaat per deel.',
    Icon: FileStack,
    bullets: [
      'Zonder **Regels opslaan** (knop op Posten) blijven **Resultaat** en **Rapportage** dicht — bewust: eerst metingen en posten goed zetten.',
      'Met **bouwcluster** groepeer je regels voor de samenvatting op Resultaat; je kunt een cluster ook **in één keer hernoemen**.',
      'De catalogus is per zoekopdracht begrensd (bijv. max. 50 treffers) binnen jouw projectcontext — typ dus iets specifieker als je iets mist.',
      'Boven het prijsboek zie je **hoeveel actieve systemen en varianten** de hele bibliotheek telt (tenant-merge) — los van je huidige zoekresultaat.',
    ],
  },
  {
    id: 'drawings',
    title: 'Stap 3 — Tekening of PDF (optioneel)',
    lead: 'Een analyse van tekening of PDF kan een **voorstel** maken; jij keurt expliciet goed voordat het definitief in je postenland. Ook met bestaande posten kun je op Gegevens verder uploaden en analyseren — handig als het dossier groeit.',
    Icon: Upload,
    bullets: [
      'Geen stille overstap van analyse naar vaste cijfers: je keurt op **Posten** met een **bevestigingsmodal**.',
      '**Klanttype** stuurt vooral het **importadvies** (kern / aanbevolen / aanvullend) en de toon; de analyse zelf koppelt voor iedereen volledig aan de prijsbibliotheek. **Duidelijke bestandsnamen** helpen het model. Je kunt platen uit de bibliotheek opnieuw aanvinken voor analyse.',
      'Importeer posten **per selectie** — het conceptvoorstel wordt aangevuld tot alles uit de analyse is geïmporteerd.',
      'Zorg dat **gegevens en regio** kloppen en sla op **vóór** je analyse start — dan sluit het voorstel beter aan.',
      'Heeft het dossier al opgeslagen regels? Dan kies je **toevoegen**, **vervangen** of **samenvoegen** (dubbele systeem+variant+locatie → aantallen optellen).',
      'Meer uitleg: footer onder **Handleidingen** → Tekening & PDF; bij storingen **Dienststatus** en **Problemen oplossen**.',
    ],
  },
  {
    id: 'totals',
    title: 'Stap 4 — Resultaat & rapportage',
    lead: 'Met opgeslagen posten rekent CalqTool door naar totalen en onderbouwing. **Resultaat** is je controle-tab; **Rapportage** is waar je **calculatie** en **offerte** uit dezelfde bron haalt — elk met een eigen knoppenrij, zodat je niet door elkaar haalt.',
    Icon: BarChart3,
    bullets: [
      '**Resultaat:** totalen en prijsopbouw (materialen, arbeid, materieel) — per klanttype iets andere nadruk, maar altijd met dezelfde onderliggende library-logica.',
      '**Rapportage — calculatie:** download **Calculatie CSV**, **Calculatie Excel** (UTF-8 met BOM) of **Calculatie PDF**; stuur **exportinstellingen** (bijv. BTW, scheidingsteken, voorbehoud, per-deel in PDF) eerst goed aan.',
      '**Rapportage — offerte:** **Offerte genereren** voor de preview; **Print / PDF** voor een schone PDF via je browser. **Offerte-PDF** (server) staat in **Exportinstellingen** — bewust iets weggewerkt zodat het scherm rustig blijft.',
      '**Calculatie-export** (CSV, Excel, begroting-PDF) staat op dezelfde tab maar in een eigen blok — zie ook **Export & offerte** onder Handleidingen in de footer.',
      'Dossiers met een **open tekening-voorstel** zie je op het dashboard met een badge — niets vergeten.',
    ],
  },
  {
    id: 'done',
    title: 'Je bent klaar om te starten',
    lead: 'Je weet nu het ritme: gegevens goed → posten vastleggen → resultaat checken → rapportage (calculatie en/of offerte). Dat is precies de volgorde van je tabs — en je kunt onderweg niet per ongeluk “de weg kwijtraken”.',
    Icon: CheckCircle2,
    bullets: [
      '**Dashboard** → **Nieuw dossier** → projectpagina, dan de vier tabs doorlopen zoals het voor jou uitkomt.',
      'Deze intro kun je altijd heropenen via de footer (**Introductie**) of met `?replay=1` in de URL als je hem opnieuw wilt doorlopen.',
      'Vragen? Gebruik de chat rechtsonder of de pagina **Contact**.',
    ],
    tip: 'Rond af om de intro-banner op je dashboard te verbergen — de intro blijft gewoon beschikbaar via Introductie.',
  },
];

function ProgressDots({ step, total }: { step: number; total: number }) {
  return (
    <div
      className="mb-6 flex justify-center gap-2"
      role="progressbar"
      aria-valuenow={step + 1}
      aria-valuemin={1}
      aria-valuemax={total}
      aria-label={`Stap ${step + 1} van ${total}`}
    >
      {Array.from({ length: total }, (_, i) => (
        <span
          key={i}
          className={`h-2.5 rounded-full transition ${
            i === step ? 'w-8 bg-primary' : i < step ? 'w-2.5 bg-primary/50' : 'w-2.5 bg-border'
          }`}
        />
      ))}
    </div>
  );
}

function CompletionGate({ onReplay }: { onReplay: () => void }) {
  return (
    <div className="mx-auto max-w-lg rounded-xl border border-border bg-surface-card p-8 text-center shadow-sm">
      <CheckCircle2 className="mx-auto mb-4 h-14 w-14 text-primary" aria-hidden />
      <h2 className="mb-2 text-xl font-bold text-text">Intro al afgerond</h2>
      <p className="mb-6 text-sm text-text-muted">
        Je hebt de rondleiding eerder voltooid of overgeslagen. Wil je hem opnieuw doorlopen?
      </p>
      <div className="flex flex-col gap-3 sm:flex-row sm:justify-center">
        <button
          type="button"
          onClick={onReplay}
          className="rounded-lg border border-border bg-surface px-5 py-2.5 text-sm font-medium text-text hover:bg-surface-card"
        >
          Opnieuw doorlopen
        </button>
        <Link
          to="/dashboard"
          className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary px-5 py-2.5 text-sm font-medium text-white hover:bg-primary-alt"
        >
          Naar dashboard
          <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </div>
  );
}

function initialScreen(): 'wizard' | 'gate' {
  if (typeof window === 'undefined') return 'wizard';
  try {
    const replay = new URLSearchParams(window.location.search).get('replay') === '1';
    if (replay) return 'wizard';
    return isOnboardingDone() ? 'gate' : 'wizard';
  } catch {
    return 'wizard';
  }
}

export function OnboardingWizard() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [screen, setScreen] = useState<'wizard' | 'gate'>(initialScreen);
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (searchParams.get('replay') === '1') {
      setScreen('wizard');
      setStep(0);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const total = STEPS.length;
  const def = STEPS[step];

  const goNext = () => {
    if (step < total - 1) setStep((s) => s + 1);
  };
  const goBack = () => setStep((s) => Math.max(0, s - 1));

  const handleSkip = () => {
    markOnboardingSkipped();
    navigate('/dashboard');
  };

  const handleFinish = () => {
    markOnboardingComplete();
    navigate('/projects/intake');
  };

  const handleReplay = () => {
    resetOnboarding();
    setStep(0);
    setScreen('wizard');
  };

  if (screen === 'gate') {
    return (
      <div className="animate-page-enter pb-16">
        <SeoMeta
          path="/onboarding"
          noindex
          title="Introductie"
          description="Introductie CalqTool™: ontdek Gegevens, Posten, Resultaat en Rapportage — prijsbibliotheek, calculatie, export en offerte in één workflow."
        />
        <AnimatedSection delay={0.05}>
          <Breadcrumbs items={[{ label: 'Home', href: '/' }, { label: 'Introductie' }]} />
          <h1 className="mb-8 text-2xl font-bold text-text">Introductie</h1>
          <CompletionGate onReplay={handleReplay} />
        </AnimatedSection>
      </div>
    );
  }

  const Icon = def.Icon;

  return (
    <div className="animate-page-enter pb-24 md:pb-12">
      <SeoMeta
        path="/onboarding"
        noindex
        title="Introductie — stap voor stap"
        description="Stap-voor-stap door CalqTool™: vier tabs met vaste navigatie, posten uit de NL-bibliotheek, totalen, calculatie-export (CSV/Excel/PDF) en offerte — in enkele minuten klaar."
      />
      <AnimatedSection delay={0.05}>
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <Breadcrumbs items={[{ label: 'Home', href: '/' }, { label: 'Introductie' }]} />
          <button
            type="button"
            onClick={handleSkip}
            className="text-sm font-medium text-text-muted underline-offset-2 hover:text-text hover:underline"
          >
            Intro overslaan
          </button>
        </div>

        <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-text-muted">
          Stap {step + 1} van {total}
        </p>
        <h1 className="mb-2 text-2xl font-bold text-text md:text-3xl">{def.title}</h1>
        <p className="mb-6 max-w-2xl text-sm text-text-muted md:text-base">{renderBulletWithBold(def.lead)}</p>

        <ProgressDots step={step} total={total} />

        <div className="mx-auto max-w-2xl rounded-2xl border border-border bg-surface-card p-6 shadow-sm md:p-8">
          <div className="mb-6 flex justify-center">
            <div className="flex h-20 w-20 items-center justify-center rounded-2xl bg-primary/12 text-primary ring-1 ring-primary/20">
              <Icon className="h-10 w-10" strokeWidth={1.5} aria-hidden />
            </div>
          </div>
          <ul className="space-y-3 text-sm text-text md:text-base">
            {def.bullets.map((b, i) => (
              <li key={i} className="flex gap-3">
                <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" aria-hidden />
                <span>{renderBulletWithBold(b)}</span>
              </li>
            ))}
          </ul>
          {def.tip && (
            <p className="mt-6 rounded-lg border border-primary/20 bg-primary/5 px-4 py-3 text-xs text-text-muted md:text-sm">
              <strong className="text-text">Tip: </strong>
              {renderBulletWithBold(def.tip)}
            </p>
          )}
        </div>

        <div className="mx-auto mt-8 flex max-w-2xl flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <button
            type="button"
            onClick={goBack}
            disabled={step === 0}
            className="order-2 rounded-lg border border-border bg-surface-card px-6 py-3 text-sm font-medium text-text disabled:opacity-40 sm:order-1"
          >
            Terug
          </button>
          <div className="order-1 flex flex-col gap-3 sm:order-2 sm:flex-row sm:gap-3">
            {step < total - 1 ? (
              <button
                type="button"
                onClick={goNext}
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary px-6 py-3 text-sm font-medium text-white hover:bg-primary-alt"
              >
                Volgende
                <ArrowRight className="h-4 w-4" />
              </button>
            ) : (
              <button
                type="button"
                onClick={handleFinish}
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary px-6 py-3 text-sm font-medium text-white hover:bg-primary-alt"
              >
                Afronden &amp; begeleid dossier starten
                <ArrowRight className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        <p className="mx-auto mt-6 max-w-2xl text-center text-xs text-text-muted">
          <button
            type="button"
            onClick={() => {
              markOnboardingSkipped();
              navigate('/dashboard');
            }}
            className="text-primary underline-offset-2 hover:underline"
          >
            Naar dashboard zonder nieuw dossier
          </button>
        </p>
      </AnimatedSection>
    </div>
  );
}
